import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import DropArea from "../../pages/instances/production/dashboard/purchasing-popup/drag-and-drop/DropArea";
import FilesPopup from "../EditProduct/FilesPopup";
import { getCustomerFilesWithMeta } from "../../services/customerFiles";

const imageUrlPattern = /\.(png|jpe?g|gif|webp|bmp|svg)(\?.*)?$/i;
const pdfUrlPattern = /\.pdf(\?.*)?$/i;
const MEASUREMENT_UNIT_OPTIONS = ["mm", "cm", "inch", "px"];
const OUTPUT_FORMAT_OPTIONS = ["ai", "eps", "pdf", "svg", "png", "jpg", "jpeg", "psd", "dst", "emb", "pes", "zip"];
const PRIORITY_OPTIONS = ["Same Day", "Next Day", "3 Days"];

const getPreviewUrl = ({ file = null, statusItem = null, context = null } = {}) =>
  file?.previewUrl ||
  file?.downloadUrl ||
  context?.sourceFilePath ||
  statusItem?.fileUrls?.[0] ||
  statusItem?.productUrl ||
  context?.originalImage ||
  "";

const isImagePreview = (url = "") => imageUrlPattern.test(String(url || ""));
const isPdfPreview = (url = "") => pdfUrlPattern.test(String(url || ""));

const buildFileName = (url = "", fallback = "Artwork file") => {
  try {
    const parsed = new URL(String(url || ""));
    const parts = parsed.pathname.split("/").filter(Boolean);
    return parts[parts.length - 1] || fallback;
  } catch {
    const parts = String(url || "").split("/").filter(Boolean);
    return parts[parts.length - 1] || fallback;
  }
};

function ArtworkAssetCard({ url = "", name = "", variant = "current", onRemove = null, removable = false, disabled = false, testId = "" }) {
  const label = name || buildFileName(url);
  const isImage = isImagePreview(url);
  const isPdf = isPdfPreview(url);

  return (
    <Box
      sx={{
        position: "relative",
        border: "1px solid #e5e7eb",
        borderRadius: 1.5,
        background: "#fff",
        p: 1,
        display: "flex",
        flexDirection: "column",
        gap: 0.75,
      }}
    >
      {removable ? (
        <IconButton
          size="small"
          sx={{ position: "absolute", top: 6, right: 6, background: "#fff" }}
          onClick={onRemove}
          disabled={disabled}
        >
          <CloseIcon fontSize="inherit" />
        </IconButton>
      ) : null}
      <Box
        data-testid={testId}
        sx={{
          height: 120,
          maxHeight: 120,
          borderRadius: 1,
          border: "1px solid #e5e7eb",
          background: "#f8fafc",
          overflow: "hidden",
          display: "grid",
          placeItems: "center",
        }}
      >
        {isImage ? (
          <Box
            component="img"
            src={url}
            alt={label}
            sx={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
          />
        ) : (
          <Typography variant="caption" sx={{ color: "text.secondary", textAlign: "center", px: 1 }}>
            {isPdf ? "PDF File" : "File"}
          </Typography>
        )}
      </Box>
      <Typography variant="caption" sx={{ wordBreak: "break-word", fontWeight: 600 }}>
        {label}
      </Typography>
      <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
        <Chip size="small" label={variant === "current" ? "Current Artwork" : "Replacement"} variant="outlined" />
        <Button size="small" variant="text" href={url} target="_blank" rel="noopener noreferrer">
          {isImage ? "View" : "Open"}
        </Button>
      </Box>
    </Box>
  );
}

export default function BinatedLifecycleRevisionModal({
  open,
  file,
  note,
  title = "Request Revision",
  statusItem = null,
  context = null,
  contextLoading = false,
  submitDisabledReason = "",
  submitError = "",
  submitLabel = "Submit Revision",
  onNoteChange,
  formData = null,
  onFormChange,
  currentArtworkUrls = [],
  replacementFiles = [],
  replacementInternalFiles = [],
  customerId = null,
  files = [],
  onFilesAdd,
  onFileRemove,
  onInternalFileSelect,
  onInternalFileRemove,
  onClose,
  onSubmit,
  submitting = false,
  showFileUpload = true,
}) {
  const previewUrl = getPreviewUrl({ file, statusItem, context });
  const isStatusItemUpdate = Boolean(statusItem);
  const placementLabel = [
    statusItem?.placementCode,
    statusItem?.placementName && statusItem?.placementName !== statusItem?.placementCode
      ? statusItem.placementName
      : "",
  ]
    .filter(Boolean)
    .join(" - ");
  const currentArtwork = (Array.isArray(currentArtworkUrls) && currentArtworkUrls.length
    ? currentArtworkUrls
    : (previewUrl ? [previewUrl] : [])
  ).filter(Boolean);

  const [customerFiles, setCustomerFiles] = useState([]);
  const [customerFilesLoading, setCustomerFilesLoading] = useState(false);
  const [internalFilePickerOpen, setInternalFilePickerOpen] = useState(false);

  const replacementPreviewUrls = useMemo(
    () =>
      replacementFiles.map((replacementFile) =>
        replacementFile ? URL.createObjectURL(replacementFile) : ""
      ),
    [replacementFiles]
  );

  useEffect(() => {
    return () => {
      replacementPreviewUrls.filter(Boolean).forEach((url) => {
        try {
          URL.revokeObjectURL(url);
        } catch {
          // ignore revoke errors
        }
      });
    };
  }, [replacementPreviewUrls]);

  const replacementArtwork = useMemo(
    () =>
      replacementFiles.map((replacementFile, idx) => ({
        name: replacementFile?.name || `Replacement file ${idx + 1}`,
        url: replacementPreviewUrls[idx] || "",
        kind: "upload",
      })),
    [replacementFiles, replacementPreviewUrls]
  );
  const internalReplacementArtwork = useMemo(
    () =>
      (Array.isArray(replacementInternalFiles) ? replacementInternalFiles : []).map((entry, idx) => ({
        name: entry?.fileName || `Internal file ${idx + 1}`,
        url: String(entry?.previewUrl || entry?.filePath || entry?.url || "").trim(),
        kind: "internal",
      })),
    [replacementInternalFiles]
  );
  const customerFileGroups = useMemo(
    () => ({
      "Client Files": customerFiles,
    }),
    [customerFiles]
  );

  useEffect(() => {
    if (!open || !isStatusItemUpdate || !customerId) {
      setCustomerFiles([]);
      setCustomerFilesLoading(false);
      return;
    }

    let cancelled = false;
    const loadCustomerFiles = async () => {
      try {
        setCustomerFilesLoading(true);
        const filesResponse = await getCustomerFilesWithMeta({ customerId });
        if (!cancelled) {
          setCustomerFiles(Array.isArray(filesResponse) ? filesResponse : []);
        }
      } catch {
        if (!cancelled) setCustomerFiles([]);
      } finally {
        if (!cancelled) setCustomerFilesLoading(false);
      }
    };

    loadCustomerFiles();
    return () => {
      cancelled = true;
    };
  }, [customerId, isStatusItemUpdate, open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        {statusItem ? (
          <Box sx={{ mb: 1.5, p: 1.25, border: "1px solid #e5e7eb", borderRadius: 1.5, background: "#f8fafc" }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
              Placement Details
            </Typography>
            <Typography variant="caption" sx={{ display: "block" }}>
              SKU: {statusItem?.productSku || "-"}
            </Typography>
            <Typography variant="caption" sx={{ display: "block" }}>
              Product: {statusItem?.productName || "-"}
            </Typography>
            {statusItem?.garment ? (
              <Typography variant="caption" sx={{ display: "block" }}>
                Garment / Variant: {statusItem.garment}
              </Typography>
            ) : null}
            <Typography variant="caption" sx={{ display: "block" }}>
              Placement: {placementLabel || "-"}
            </Typography>
            <Typography variant="caption" sx={{ display: "block" }}>
              Status / Stage: {statusItem?.rawStatus || "-"} / {statusItem?.rawStage || "-"}
            </Typography>
            {statusItem?.dueAt ? (
              <Typography variant="caption" sx={{ display: "block" }}>
                Due: {new Date(statusItem.dueAt).toLocaleString()}
              </Typography>
            ) : null}
            {(statusItem?.providerOrderId || statusItem?.poUuid || statusItem?.parentOrderUuid || statusItem?.placementUuid) && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" sx={{ display: "block", fontWeight: 700 }}>
                  Provider Metadata
                </Typography>
                {statusItem?.providerOrderId ? <Typography variant="caption" sx={{ display: "block" }}>so_uuid: {statusItem.providerOrderId}</Typography> : null}
                {statusItem?.poUuid ? <Typography variant="caption" sx={{ display: "block" }}>poUuid: {statusItem.poUuid}</Typography> : null}
                {statusItem?.parentOrderUuid ? <Typography variant="caption" sx={{ display: "block" }}>parentOrderUuid: {statusItem.parentOrderUuid}</Typography> : null}
                {statusItem?.placementUuid ? <Typography variant="caption" sx={{ display: "block" }}>placementUuid: {statusItem.placementUuid}</Typography> : null}
              </Box>
            )}
          </Box>
        ) : (
          <Typography variant="body2" sx={{ mb: 1 }}>
            {file?.fileName ? `File: ${file.fileName}` : "Add revision notes for Binated."}
          </Typography>
        )}

        {file?.version != null ? (
          <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1 }}>
            Version: v{file.version}
          </Typography>
        ) : null}

        {isStatusItemUpdate ? (
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" sx={{ display: "block", fontWeight: 700, mb: 0.75 }}>
              Current Artwork
            </Typography>
            {currentArtwork.length ? (
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }, gap: 1 }}>
                {currentArtwork.map((url, idx) => (
                  <ArtworkAssetCard
                    key={`${url}-${idx}`}
                    url={url}
                    name={buildFileName(url, `Current artwork ${idx + 1}`)}
                    variant="current"
                    testId="current-artwork-preview"
                  />
                ))}
              </Box>
            ) : (
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                No current artwork URLs available.
              </Typography>
            )}
          </Box>
        ) : previewUrl ? (
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" sx={{ display: "block", fontWeight: 700, mb: 0.5 }}>
              Current Artwork Preview
            </Typography>
            {isImagePreview(previewUrl) ? (
              <Box
                component="img"
                src={previewUrl}
                alt={statusItem?.productName || file?.fileName || "Binated artwork preview"}
                sx={{
                  width: "100%",
                  maxHeight: 220,
                  objectFit: "contain",
                  borderRadius: 1,
                  border: "1px solid #e5e7eb",
                  background: "#fff",
                }}
              />
            ) : (
              <Button variant="outlined" size="small" href={previewUrl} target="_blank" rel="noopener noreferrer">
                Open Current Artwork
              </Button>
            )}
          </Box>
        ) : null}

        {contextLoading ? (
          <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1.5 }}>
            Loading mapped artwork context...
          </Typography>
        ) : context ? (
          <Box sx={{ mb: 1.5, p: 1.25, border: "1px solid #e5e7eb", borderRadius: 1, background: "#f8fafc" }}>
            <Typography variant="caption" sx={{ display: "block", fontWeight: 700, mb: 0.5 }}>
              Mapped Artwork Context
            </Typography>
            <Typography variant="caption" sx={{ display: "block" }}>
              {`${context?.documentType || "Document"} ${context?.documentId || ""}`.trim()}
            </Typography>
            {context?.logoName ? <Typography variant="caption" sx={{ display: "block" }}>Logo: {context.logoName}</Typography> : null}
            {context?.location ? <Typography variant="caption" sx={{ display: "block" }}>Location: {context.location}</Typography> : null}
            {context?.customerRejectedComment ? (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" sx={{ display: "block", fontWeight: 700 }}>
                  Customer requested changes
                </Typography>
                <Typography variant="caption" sx={{ display: "block", whiteSpace: "pre-wrap" }}>
                  {context.customerRejectedComment}
                </Typography>
              </Box>
            ) : null}
          </Box>
        ) : null}

        {submitDisabledReason ? (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            {submitDisabledReason}
          </Alert>
        ) : null}

        {submitError ? (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            {submitError}
          </Alert>
        ) : null}

        {isStatusItemUpdate ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Grid container spacing={1.25}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Placement Code"
                  fullWidth
                  value={formData?.placement_code || ""}
                  onChange={(e) => onFormChange?.("placement_code", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Reference"
                  fullWidth
                  value={formData?.reference || ""}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Process Type"
                  fullWidth
                  value={formData?.process_type || ""}
                  onChange={(e) => onFormChange?.("process_type", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Garment"
                  fullWidth
                  value={formData?.garment || ""}
                  onChange={(e) => onFormChange?.("garment", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Height"
                  fullWidth
                  value={formData?.height ?? ""}
                  onChange={(e) => onFormChange?.("height", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Width"
                  fullWidth
                  value={formData?.width ?? ""}
                  onChange={(e) => onFormChange?.("width", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  label="Measurement Unit"
                  fullWidth
                  value={formData?.measurement_unit || "inch"}
                  onChange={(e) => onFormChange?.("measurement_unit", e.target.value)}
                >
                  {MEASUREMENT_UNIT_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>{option}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Desired Output Format"
                  fullWidth
                  value={formData?.output_file_format || "pdf"}
                  onChange={(e) => onFormChange?.("output_file_format", e.target.value)}
                >
                  {OUTPUT_FORMAT_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>{option}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Priority"
                  fullWidth
                  value={formData?.priority || "Next Day"}
                  onChange={(e) => onFormChange?.("priority", e.target.value)}
                >
                  {PRIORITY_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>{option}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  multiline
                  minRows={3}
                  fullWidth
                  value={formData?.description || ""}
                  onChange={(e) => onFormChange?.("description", e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" sx={{ display: "block", fontWeight: 700, mb: 0.75 }}>
                  Replacement Artwork
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<FolderOpenIcon />}
                    onClick={() => setInternalFilePickerOpen(true)}
                    disabled={submitting || customerFilesLoading || !customerId}
                  >
                    {customerFilesLoading ? "Loading Files..." : "Select Internal File"}
                  </Button>
                  {!customerId ? (
                    <Typography variant="caption" sx={{ color: "text.secondary", alignSelf: "center" }}>
                      Attach a customer to this order to browse internal files.
                    </Typography>
                  ) : null}
                </Box>
                <DropArea
                  label="Drag 'n' drop replacement artwork here, or click to select"
                  sublabel="Upload one or more replacement artwork files"
                  multiple
                  disabled={submitting}
                  onFiles={(nextFiles) => onFilesAdd?.(nextFiles)}
                />
                {[...internalReplacementArtwork, ...replacementArtwork].length > 0 ? (
                  <Box sx={{ mt: 1, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }, gap: 1 }}>
                    {internalReplacementArtwork.map((entry, idx) => (
                      <ArtworkAssetCard
                        key={`internal-${entry.url}-${idx}`}
                        url={entry.url}
                        name={entry.name}
                        variant="replacement"
                        removable
                        disabled={submitting}
                        onRemove={() => onInternalFileRemove?.(idx)}
                        testId="replacement-artwork-preview"
                      />
                    ))}
                    {replacementArtwork.map((entry, idx) => (
                      <ArtworkAssetCard
                        key={`${entry.name}-${idx}`}
                        url={entry.url}
                        name={entry.name}
                        variant="replacement"
                        removable
                        disabled={submitting}
                        onRemove={() => onFileRemove?.(idx)}
                        testId="replacement-artwork-preview"
                      />
                    ))}
                  </Box>
                ) : null}
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Artwork URL(s)"
                  multiline
                  minRows={2}
                  fullWidth
                  value={formData?.url || ""}
                  onChange={(e) => onFormChange?.("url", e.target.value)}
                  placeholder="Advanced: enter one URL per line or comma-separated"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Colors"
                  fullWidth
                  value={formData?.colors || ""}
                  onChange={(e) => onFormChange?.("colors", e.target.value)}
                  placeholder="Comma-separated colors"
                />
              </Grid>
            </Grid>

            <TextField
              label="Internal Note"
              multiline
              minRows={3}
              fullWidth
              value={note}
              onChange={(e) => onNoteChange?.(e.target.value)}
              placeholder="Internal note for Elite Core only. This is not sent to Binated."
            />
          </Box>
        ) : (
          <>
            <TextField
              label="Revision Notes"
              multiline
              minRows={4}
              fullWidth
              required
              value={note}
              onChange={(e) => onNoteChange?.(e.target.value)}
              placeholder="Describe what should change."
            />
            {showFileUpload ? (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Upload Files (Optional)
                </Typography>
                <DropArea
                  label="Drag 'n' drop files here, or click to select"
                  sublabel="Optional attachments for the revision request"
                  multiple
                  disabled={submitting}
                  onFiles={(nextFiles) => onFilesAdd?.(nextFiles)}
                />
                {files.length > 0 ? (
                  <Box sx={{ mt: 1, display: "grid", gap: 0.5 }}>
                    {files.map((revisionFile, idx) => (
                      <Box
                        key={`${revisionFile?.name || "file"}-${idx}`}
                        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", border: "1px solid #e5e7eb", borderRadius: 1, px: 1, py: 0.5 }}
                      >
                        <Typography variant="caption" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", pr: 1 }}>
                          {revisionFile?.name || `File ${idx + 1}`}
                        </Typography>
                        <IconButton size="small" onClick={() => onFileRemove?.(idx)} disabled={submitting}>
                          <CloseIcon fontSize="inherit" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                ) : null}
              </Box>
            ) : null}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          onClick={onSubmit}
          disabled={submitting || (!isStatusItemUpdate && !String(note || "").trim()) || Boolean(submitDisabledReason)}
        >
          {submitting ? "Submitting..." : submitLabel}
        </Button>
      </DialogActions>
      <FilesPopup
        open={internalFilePickerOpen}
        onClose={() => setInternalFilePickerOpen(false)}
        onImageSelect={() => {}}
        onSelectFile={(selectedFile) => {
          onInternalFileSelect?.(selectedFile);
          setInternalFilePickerOpen(false);
        }}
        imageGroups={customerFileGroups}
        selectedImage=""
        customerId={customerId || null}
        allowUpload={false}
      />
    </Dialog>
  );
}
